import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Modal from 'react-modal';
import { tracker } from '$utils/tracker';

import { ReactComponent as CloseBlue } from '$icons/CloseBlue.svg';
import {
  useGetInterviewSlotsQuery,
  useSetInterviewSlotsMutation,
} from '$store/modules/interview';
import classes from './CalendarModal.module.css';
import { LeftCalendarModal } from './LeftCalendarModal';
import { RightCalendarModal } from './RightCalendarModal/RightCalendarModal';
import SuccessAnimation from './SuccessAnimation/SuccessAnimation';
import Spinner from '../../Spinner/Spinner';
import { DAYJS_FORMAT_DATETIME, TIMEZONES } from '../../../constants/datetimes';
import { diffOffset } from '../../../utils/utcDifference';
import { useAppDispatch } from '../../../hooks';
import { companiesApi } from '../../../store/modules/companies';
import { candidatesApi } from '../../../store/modules/candidates';
import { SetSchedulesType } from './CalendarModalTypes';

dayjs.extend(utc);
dayjs.extend(timezone);

interface CalendarModalProps {
  candidateId: number;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  candidatePositionStatusId: number;
  handleOk: () => void;
}
export const CalendarModal = ({
  candidateId,
  isOpen,
  setIsOpen,
  candidatePositionStatusId,
  handleOk,
}: CalendarModalProps) => {
  Modal.setAppElement(document.getElementById('root') as HTMLElement);
  const [schedules, setSchedules] = useState<SetSchedulesType[]>([]);
  const [done, setDone] = useState(false);
  const [timeZone, setTimeZone] = useState(TIMEZONES.EASTERN);

  const { data: interviewData, isLoading: isLoadingInterviewData } =
    useGetInterviewSlotsQuery();
  const [setInterview] = useSetInterviewSlotsMutation();

  const dispatch: any = useAppDispatch();

  useEffect(() => {
    if (!isLoadingInterviewData) {
      const interviewParsedData = interviewData?.map((singleSchedule) => {
        const difference = diffOffset(
          timeZone,
          singleSchedule.Timezone === null
            ? TIMEZONES.EASTERN
            : singleSchedule.Timezone
        );
        return {
          id: singleSchedule.Id,
          status: singleSchedule?.Interview?.Status,
          date: dayjs(singleSchedule.StartDate.replace('Z', ''))
            .add(difference, 'h')
            .tz(timeZone, true),
        };
      });

      setSchedules(interviewParsedData ?? []);
    }
  }, [timeZone, interviewData, isLoadingInterviewData]);

  const submitSchedules = () => {
    const interviewSchedules = schedules.map((singleSchedule) => {
      return {
        StartDate: singleSchedule.date.format(DAYJS_FORMAT_DATETIME),
        EndDate: singleSchedule.date
          .add(1, 'hour') // assuming 1 hour of duration
          .format(DAYJS_FORMAT_DATETIME),
        Timezone: timeZone,
      };
    });
    setInterview({
      schedules: interviewSchedules,
      candidateId,
      candidatePositionStatusId,
    }).then(() => {
      tracker.emit('Interview Requested', {
        candidate_id: candidateId,
      });
      dispatch(companiesApi.util.invalidateTags(['PositionsList']));
      dispatch(candidatesApi.util.invalidateTags(['Candidates']));
    });
    setDone(true);
    setTimeout(() => {
      setIsOpen(false);
    }, 1000);
    handleOk();
  };

  const handleClose = () => {
    setIsOpen(false);
    setDone(false);
  };

  return (
    <Modal
      className={classes.modal}
      overlayClassName={classes.overlay}
      isOpen={isOpen}
      onRequestClose={handleClose}
      ariaHideApp={false}
    >
      {!isLoadingInterviewData && (
        <div
          className={`items-stretch overflow-y-auto font-mulish ${classes.container}`}
        >
          <CloseBlue
            onClick={() => setIsOpen(false)}
            className="block md:hidden cursor-pointer w-8 h-8 absolute top-1 right-1"
          />
          {!done ? (
            <div className="flex flex-col w-full gap-4">
              <div className="flex w-full">
                <LeftCalendarModal
                  setSchedules={setSchedules}
                  schedules={schedules}
                  setTimezone={setTimeZone}
                  timezone={timeZone}
                />
                <div className={classes.divider} />
                <RightCalendarModal
                  schedules={schedules}
                  setSchedules={setSchedules}
                />
              </div>
              <div className="flex w-full gap-4 items-center justify-end mt-4">
                <button
                  onClick={handleClose}
                  className="text-employer-primary text-sm font-bold px-5 py-4"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={submitSchedules}
                  className="bg-employer-primary text-white text-sm font-bold rounded-full px-5 py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                  disabled={schedules.length === 0}
                >
                  Request Interview
                </button>
              </div>
            </div>
          ) : (
            <SuccessAnimation />
          )}
        </div>
      )}
      {isLoadingInterviewData && (
        <div
          style={{ display: 'flex', justifyContent: 'center', height: '100%' }}
        >
          <Spinner height="35rem" />
        </div>
      )}
    </Modal>
  );
};
